import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    AlertCircle,
    Building2,
    CheckCircle,
    LoaderCircle,
    LockKeyhole,
    ShieldCheck
} from 'lucide-react';
import { useAuth } from '../../context/UnifiedAuthContext.jsx';
import { authAPI } from '../../utils/api.js';
import { portalDashboardPath, restorePortalAttempt, savePortalAttempt, watchPortalLogin } from '../../utils/portalLogin.js';
import './Login.scss';

const GoogleMark = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="login__google-mark">
        <path fill="#4285f4" d="M21.6 12.23c0-.71-.06-1.4-.18-2.07H12v3.92h5.38a4.6 4.6 0 0 1-2 3.02v2.54h3.24c1.9-1.75 2.98-4.33 2.98-7.41Z" />
        <path fill="#34a853" d="M12 22c2.7 0 4.98-.9 6.63-2.36l-3.24-2.54c-.9.6-2.05.96-3.39.96-2.61 0-4.82-1.76-5.61-4.13H3.04v2.62A10 10 0 0 0 12 22Z" />
        <path fill="#fbbc05" d="M6.39 13.93A6.02 6.02 0 0 1 6.07 12c0-.67.12-1.32.32-1.93V7.45H3.04A10 10 0 0 0 2 12c0 1.61.39 3.14 1.04 4.55l3.35-2.62Z" />
        <path fill="#ea4335" d="M12 5.94c1.47 0 2.78.5 3.82 1.49l2.88-2.88A9.65 9.65 0 0 0 12 2a10 10 0 0 0-8.96 5.45l3.35 2.62C7.18 7.7 9.39 5.94 12 5.94Z" />
    </svg>
);

const Login = () => {
    const routeLocation = useLocation();
    const navigate = useNavigate();
    const { technician, urlParams } = useAuth();
    const queryParams = useMemo(
        () => new URLSearchParams(routeLocation.search),
        [routeLocation.search]
    );
    const callbackStatus = queryParams.get('status');
    const mappedUser = queryParams.get('user');
    const isEmbedded = window.self !== window.top;

    const [locationId, setLocationId] = useState(queryParams.get('location') || '');
    const [error, setError] = useState('');
    const [portalAttempt, setPortalAttempt] = useState(() => isEmbedded
        ? restorePortalAttempt({ locationId: queryParams.get('location'), userId: mappedUser })
        : null);
    const [showPortalRecovery, setShowPortalRecovery] = useState(false);
    const [portalRecoveryMessage, setPortalRecoveryMessage] = useState('');
    const [portalRecoveryPending, setPortalRecoveryPending] = useState(false);
    const [portalRetry, setPortalRetry] = useState(0);
    const activeAttempt = useRef(portalAttempt);
    const recoveryOperation = useRef(null);
    const mounted = useRef(false);
    const [requestState, setRequestState] = useState(
        callbackStatus === 'requested' ? 'requested' : 'idle'
    );
    const [showRequestPrompt, setShowRequestPrompt] = useState(
        callbackStatus === 'not_invited'
    );

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
            recoveryOperation.current = null;
        };
    }, []);

    useEffect(() => {
        if (!technician) return;

        const requestedLocation = queryParams.get('location');
        const requestedUser = queryParams.get('user');
        if (
            !requestedLocation
            || !requestedUser
            || requestedLocation !== urlParams.location
            || requestedUser !== urlParams.user
        ) {
            return;
        }

        const dashboardParams = new URLSearchParams();
        dashboardParams.set('location', requestedLocation);
        dashboardParams.set('user', requestedUser);
        if (urlParams.integration) dashboardParams.set('integration', urlParams.integration);

        const search = dashboardParams.toString();
        navigate(`/dashboard${search ? `?${search}` : ''}`, { replace: true });
    }, [navigate, queryParams, technician, urlParams]);

    useEffect(() => {
        if (!portalAttempt) return;
        let completedAt;
        let completionDeadline;
        let cancelled = false;
        const stopWithError = (message) => {
            if (cancelled || activeAttempt.current !== portalAttempt) return true;
            cancelled = true;
            activeAttempt.current = null;
            recoveryOperation.current = null;
            savePortalAttempt(null);
            setPortalAttempt(null);
            setShowPortalRecovery(false);
            setPortalRecoveryPending(false);
            setError(message);
            return true;
        };
        const stop = watchPortalLogin({
            flowId: portalAttempt.flowId,
            timeoutMs: portalAttempt.expiresAt - Date.now(),
            onTimeout: () => stopWithError('Sign-in timed out. Please try again.'),
            onStatus: async (result, signal) => {
                if (cancelled || activeAttempt.current !== portalAttempt) return true;
                // /start may not have created the attempt yet on the first poll.
                // Unknown/expired IDs reveal no information and share a status.
                if (!['complete', 'resumed'].includes(result.status)) return false;
                if (
                    result.locationId !== portalAttempt.locationId
                    || !result.userId
                ) {
                    return stopWithError('The sign-in result does not match this location and user. Please try again.');
                }
                if (completedAt === undefined) {
                    completedAt = Date.now();
                    completionDeadline = setTimeout(() => {
                        if (cancelled || activeAttempt.current !== portalAttempt) return;
                        setShowPortalRecovery(true);
                        const permissionHint = typeof document.requestStorageAccess === 'function'
                            ? " Your browser may ask to allow Elevate's sign-in cookie in this portal."
                            : '';
                        setPortalRecoveryMessage(`Google sign-in is complete, but Elevate has not resumed here yet. Continue in this portal, or keep using the signed-in tab.${permissionHint}`);
                    }, 30000);
                }
                const verified = await authAPI.verifyPortalSession({
                    flowId: portalAttempt.flowId,
                    locationId: result.locationId,
                    userId: result.userId
                }, { signal });
                if (
                    cancelled
                    || signal.aborted
                    || activeAttempt.current !== portalAttempt
                    || portalAttempt.expiresAt <= Date.now()
                    || !verified
                ) return false;
                clearTimeout(completionDeadline);
                activeAttempt.current = null;
                recoveryOperation.current = null;
                savePortalAttempt(null);
                // Reload only this iframe. Its auth provider must verify the
                // cookie again before acknowledging that the return succeeded.
                window.location.replace(portalDashboardPath({
                    locationId: result.locationId,
                    userId: result.userId,
                    flowId: portalAttempt.flowId,
                    integration: portalAttempt.integration
                }));
                return true;
            }
        });
        return () => {
            cancelled = true;
            clearTimeout(completionDeadline);
            stop();
        };
    }, [portalAttempt, portalRetry]);

    const handlePortalContinue = async () => {
        const attempt = activeAttempt.current;
        if (!attempt || recoveryOperation.current) return;
        if (attempt.expiresAt <= Date.now()) {
            activeAttempt.current = null;
            savePortalAttempt(null);
            setPortalAttempt(null);
            setShowPortalRecovery(false);
            setError('Sign-in timed out. Please try again.');
            return;
        }

        const operation = {};
        recoveryOperation.current = operation;
        setPortalRecoveryPending(true);
        const isCurrent = () => mounted.current
            && recoveryOperation.current === operation
            && activeAttempt.current === attempt
            && attempt.expiresAt > Date.now();

        try {
            // Request only cookie access, directly in this click's activation.
            // Do not await a network request or automatically show a prompt.
            if (typeof document.requestStorageAccess === 'function') {
                await document.requestStorageAccess();
                if (!isCurrent()) return;
                setPortalRecoveryMessage('Checking your sign-in in this portal.');
            } else {
                if (!isCurrent()) return;
                setPortalRecoveryMessage('Checking your sign-in again. If it cannot resume here, you can keep using the signed-in tab.');
            }
            setError('');
            // Fetch the latest server result and use the same non-clearing,
            // scoped verification. Permission alone never authorizes access.
            setPortalRetry(value => value + 1);
        } catch {
            if (!isCurrent()) return;
            setPortalRecoveryMessage('The browser could not enable sign-in in this portal. You can keep using the signed-in tab, or try again here.');
        } finally {
            if (isCurrent()) {
                recoveryOperation.current = null;
                setPortalRecoveryPending(false);
            }
        }
    };

    const handleGoogleContinue = (event) => {
        event.preventDefault();
        const normalizedLocation = locationId.trim();

        if (!normalizedLocation) {
            setError('Enter your location ID to continue.');
            return;
        }

        setError('');
        recoveryOperation.current = null;
        setPortalRecoveryPending(false);
        setShowPortalRecovery(false);
        setPortalRecoveryMessage('');
        if (isEmbedded) {
            const flowId = window.crypto.randomUUID();
            const signInUrl = authAPI.getGoogleStartUrl(normalizedLocation, mappedUser, flowId);
            // Start the entire OAuth flow outside the frame so its nonce cookie
            // and Google's sign-in page are both created in a top-level context.
            window.open(signInUrl, '_blank', 'noopener,noreferrer');
            const attempt = {
                flowId,
                locationId: normalizedLocation,
                userId: mappedUser,
                integration: queryParams.get('integration'),
                expiresAt: Date.now() + 10 * 60 * 1000
            };
            activeAttempt.current = attempt;
            savePortalAttempt(attempt);
            setPortalAttempt(attempt);
        } else {
            window.location.assign(authAPI.getGoogleStartUrl(normalizedLocation, mappedUser));
        }
    };

    const handleLocationChange = (event) => {
        const value = event.target.value;
        setLocationId(value);
        setError('');
        activeAttempt.current = null;
        recoveryOperation.current = null;
        setPortalRecoveryPending(false);
        setShowPortalRecovery(false);
        savePortalAttempt(null);
        setPortalAttempt(null);

        const retainedParams = new URLSearchParams(routeLocation.search);
        if (value.trim()) retainedParams.set('location', value.trim());
        else retainedParams.delete('location');
        navigate(`/login${retainedParams.toString() ? `?${retainedParams.toString()}` : ''}`, {
            replace: true,
            state: routeLocation.state
        });
    };

    const handleRequestAccess = async () => {
        setRequestState('requesting');
        setError('');

        try {
            const result = await authAPI.requestGoogleAccess();
            if (!result.success || result.status !== 'requested') {
                throw new Error(result.message || 'Unable to request access.');
            }

            setRequestState('requested');
            setShowRequestPrompt(false);
        } catch (requestError) {
            setRequestState('idle');
            setError(requestError.message || 'Unable to request access. Please try again.');
        }
    };

    const returnedWithoutInvitation = callbackStatus === 'not_invited';
    const callbackFailed = ['error', 'auth_error', 'oauth_error'].includes(callbackStatus);
    const sessionError = routeLocation.state?.authError;

    return (
        <div className="login">
            <div className="login__ambient login__ambient--one" />
            <div className="login__ambient login__ambient--two" />

            <div className="login__container">
                <motion.div
                    className="login__card"
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.32 }}
                >
                    <div className="login__brand">
                        <div className="login__brand-mark">
                            <ShieldCheck size={25} />
                        </div>
                        <span>Ejento Elevate</span>
                    </div>

                    <div className="login__header">
                        <h1>Welcome back</h1>
                        <p>Choose your location, then continue securely with your invited Google account.</p>
                    </div>

                    {requestState === 'requested' ? (
                        <motion.div
                            className="login__confirmation"
                            initial={{ opacity: 0, scale: 0.98 }}
                            animate={{ opacity: 1, scale: 1 }}
                            role="status"
                        >
                            <CheckCircle size={24} />
                            <div>
                                <strong>Access requested</strong>
                                <p>An administrator will review your request. You can close this page for now.</p>
                            </div>
                        </motion.div>
                    ) : (
                        <form onSubmit={handleGoogleContinue} className="login__form">
                            <div className="login__field">
                                <label className="login__label" htmlFor="login-location">Location ID</label>
                                <div className="login__input-wrapper">
                                    <Building2 className="login__input-icon" size={19} />
                                    <input
                                        id="login-location"
                                        type="text"
                                        value={locationId}
                                        onChange={handleLocationChange}
                                        className="login__input"
                                        placeholder="Enter your location ID"
                                        autoComplete="organization"
                                        autoFocus
                                    />
                                </div>
                            </div>

                            {(returnedWithoutInvitation || callbackFailed || sessionError) && (
                                <div className="login__notice" role="status">
                                    <AlertCircle size={17} />
                                    <span>
                                        {returnedWithoutInvitation
                                            ? 'That Google account does not have an active invitation for this location.'
                                            : callbackFailed
                                                ? 'Google sign-in could not be completed. Please try again.'
                                                : 'Your previous session could not be verified. Sign in again to continue.'}
                                    </span>
                                </div>
                            )}

                            {error && (
                                <div className="login__error" role="alert">
                                    <AlertCircle size={17} />
                                    <span>{error}</span>
                                </div>
                            )}

                            <button type="submit" className="login__button">
                                <GoogleMark />
                                Continue with Google
                            </button>
                            {isEmbedded && portalAttempt && showPortalRecovery && (
                                <>
                                    <div className="login__notice" role="status">
                                        <span>{portalRecoveryMessage}</span>
                                    </div>
                                    <button
                                        type="button"
                                        className="login__button"
                                        onClick={handlePortalContinue}
                                        disabled={portalRecoveryPending}
                                    >
                                        {portalRecoveryPending ? 'Waiting for browser permission…' : 'Continue in portal'}
                                    </button>
                                </>
                            )}
                            {isEmbedded && (
                                <div className="login__notice" role="note">
                                    {portalAttempt
                                        ? showPortalRecovery
                                            ? 'Keep the signed-in tab open until Elevate is ready here.'
                                            : 'Finish Google sign-in in the new tab. Elevate will refresh here automatically.'
                                        : 'Google sign-in opens in a new tab and returns you here when complete.'}
                                </div>
                            )}
                        </form>
                    )}

                    <div className="login__footer">
                        <LockKeyhole size={15} />
                        <span>Access is limited to invited accounts for this location.</span>
                    </div>
                </motion.div>
            </div>

            <AnimatePresence>
                {showRequestPrompt && requestState !== 'requested' && (
                    <motion.div
                        className="login__modal-backdrop"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        <motion.div
                            className="login__modal"
                            role="dialog"
                            aria-modal="true"
                            aria-labelledby="request-access-title"
                            initial={{ opacity: 0, scale: 0.95, y: 12 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.97 }}
                        >
                            <div className="login__modal-icon">
                                <ShieldCheck size={24} />
                            </div>
                            <h2 id="request-access-title">Request access?</h2>
                            <p>Your Google account has not been invited to this location. Send a request to an administrator?</p>

                            {error && (
                                <div className="login__error" role="alert">
                                    <AlertCircle size={17} />
                                    <span>{error}</span>
                                </div>
                            )}

                            <div className="login__modal-actions">
                                <button
                                    type="button"
                                    className="login__modal-button login__modal-button--secondary"
                                    onClick={() => setShowRequestPrompt(false)}
                                    disabled={requestState === 'requesting'}
                                >
                                    Not now
                                </button>
                                <button
                                    type="button"
                                    className="login__modal-button login__modal-button--primary"
                                    onClick={handleRequestAccess}
                                    disabled={requestState === 'requesting'}
                                >
                                    {requestState === 'requesting' ? (
                                        <>
                                            <LoaderCircle className="login__spinner" size={17} />
                                            Sending...
                                        </>
                                    ) : 'Yes, request access'}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default Login;
